import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { FAV_CONSTANTS } from '@constants/favorites';
import { ApiResponseError, ApiResponseMeta } from '@interfaces/api';
import {
  HotFilterItem,
  HotFiltersQueryHookParams,
  HotFiltersQueryHookResult,
} from '@interfaces/api/responses/hot-filters.response';
import {
  convertAppliedHotFiltersToPayload,
  isAllowedHotFilterInUrlParams,
} from '@helpers/utils/hot-filters/hot-filters-parsing-util';
import { convertFacetsFieldsToHotFilters } from '@helpers/utils/hot-filters/hot-filters-transform-util';
import { useHotFiltersContext } from '@context/hot-filters.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { updateParamsInUrl } from '@helpers/routing';
import { PageType } from '@interfaces/common/common';
import { AxiosError } from 'axios';
import {
  buildHotFiltersAnalyticPayload,
  getHotFiltersPaginationState,
  getSlugForPageType,
} from '@helpers/utils/hot-filters/hot-filters';
import useUser from '@hooks/user/use-user';

export interface HotFiltersDataProps {
  isLoadingProducts: boolean;
  isLoadingMoreProducts: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  handleApplyFilters: (applyFilterParams: {
    triggeredByHandleApply: boolean;
    filtersApplied?: string;
    filtersDetails?: string;
    property?: string;
  }) => void;
  handleOpenFilter: (filter: HotFilterItem) => void;
  setOpenedHotFilterKey: (filterKey: string) => void;
  isError: boolean;
  error: AxiosError<{ errors: ApiResponseError }>;
}

interface UseHotFiltersDataParams<QP = HotFiltersQueryHookParams, QR = HotFiltersQueryHookResult> {
  pageType: PageType;
  queryHook: (params: QP) => QR;
  queryHookParams: Partial<QP>;
}

const useHotFiltersData = ({ pageType, queryHook, queryHookParams }: UseHotFiltersDataParams): HotFiltersDataProps => {
  const { isAuthenticated } = useUser();
  const router = useRouter();
  const { user } = useUser();

  const prevMeta = useRef<ApiResponseMeta | null>(null);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [openedHotFilterKey, setOpenedHotFilterKey] = useState<string>('');

  const { sendAnalyticEvent, updateDataLayer } = useAnalyticEvents(
    ['favourite', 'profile-favorites'].includes(pageType) ? 'favorites' : pageType,
  );

  const { sortBy, favoritesUserSlug, profileSlug, pageSize } = router?.query as {
    sortBy?: string;
    favoritesUserSlug?: string;
    profileSlug?: string;
    pageSize?: string;
  };
  const slug = getSlugForPageType(pageType, favoritesUserSlug, profileSlug, user?.id);

  const {
    totalCount,
    setPagination,
    setTotalCount,
    paginationQuery,
    setPaginationQuery,
    setHotFilters,
    setFilteredItems,
    appliedHotFilters: appliedFilters,
    setOpenedHotFilter,
    isUserSlug,
  } = useHotFiltersContext();

  const {
    data: products,
    facets,
    totalCount: filteredCount,
    hasNextPage,
    fetchNextPage,
    isLoadingMoreProducts,
    isLoadingProducts,
    meta,
    isError,
    error,
  } = queryHook({
    ...queryHookParams,
    isAuthenticated,
    userId: slug,
    sortBy,
    hotFiltersQuery: convertAppliedHotFiltersToPayload(appliedFilters),
    paginationQuery,
  });

  const isUrlParamsHasHotFilters = isAllowedHotFilterInUrlParams(appliedFilters);

  useEffect(() => {
    if (!meta) {
      return;
    }

    const { count, offset } = meta;
    // Check if the Current Offset is Valid: the user might be on a page that no longer exists
    const isInvalidPage = count && count <= offset;

    if (isInvalidPage) {
      setPaginationQuery({
        limit: parseInt(pageSize) || FAV_CONSTANTS.FAV_DEFAULT_LIMIT,
        offset: FAV_CONSTANTS.FAV_DEFAULT_OFFSET,
      });
      updateParamsInUrl({ page: '' });
    } else {
      setPagination(getHotFiltersPaginationState(meta));
      setPaginationQuery((prevQuery) => ({
        ...prevQuery,
        offset,
      }));
    }
  }, [meta]);

  useEffect(() => {
    const hotFilters = convertFacetsFieldsToHotFilters(facets?.[0], true);
    if (!isLoadingProducts) {
      setHotFilters(hotFilters);
      setFilteredItems(products);
      setTotalCount(filteredCount);
    }
  }, [facets, products, filteredCount, isLoadingProducts]);

  useEffect(() => {
    if (filtersApplied && meta && prevMeta.current !== meta) {
      const { eventName, payload } = buildHotFiltersAnalyticPayload({
        totalCount: filteredCount,
        sortBy,
        filtersApplied: appliedFilters,
        filtersDetails: appliedFilters,
        property: openedHotFilterKey,
        pageType,
        isUserSlug,
        eventType: 'filter_apply_success',
      });
      sendAnalyticEvent(eventName, payload);
      updateDataLayer({
        ...payload,
        list_products: products,
      });
      updateParamsInUrl({ page: '' });
      setFiltersApplied(false);
    }
    prevMeta.current = meta;
  }, [filtersApplied, meta, filteredCount, openedHotFilterKey, isUserSlug, pageType, products]);

  const handleOpenFilter = useCallback(
    (filter: HotFilterItem) => {
      setOpenedHotFilter(filter);
      if (filter?.filterId && (totalCount !== null || isUrlParamsHasHotFilters)) {
        setOpenedHotFilterKey(filter?.filterId);
        const { eventName, payload } = buildHotFiltersAnalyticPayload({
          totalCount,
          sortBy,
          filtersApplied: {},
          filtersDetails: appliedFilters,
          property: filter?.filterId,
          pageType,
          isUserSlug,
          eventType: 'filter_open',
        });
        sendAnalyticEvent(eventName, payload);
      }
    },
    [totalCount, isUserSlug, isUrlParamsHasHotFilters, pageType],
  );

  const handleApplyFilters = useCallback(
    ({
      triggeredByHandleApply,
      filtersApplied,
      filtersDetails,
      property,
    }: {
      triggeredByHandleApply: boolean;
      filtersApplied: string;
      filtersDetails: string;
      property: string;
    }) => {
      if (triggeredByHandleApply) {
        updateParamsInUrl({ page: '' });
        if (totalCount !== null || isUrlParamsHasHotFilters) {
          const { eventName, payload } = buildHotFiltersAnalyticPayload({
            totalCount,
            sortBy,
            filtersApplied: filtersApplied,
            filtersDetails: filtersDetails,
            property,
            pageType,
            isUserSlug,
            eventType: 'filter_apply',
          });
          sendAnalyticEvent(eventName, payload);
        }
      }
      setFiltersApplied(triggeredByHandleApply);
    },
    [totalCount, isUrlParamsHasHotFilters, sortBy],
  );

  return {
    isLoadingProducts,
    isLoadingMoreProducts,
    hasNextPage,
    fetchNextPage,
    handleApplyFilters,
    handleOpenFilter,
    setOpenedHotFilterKey,
    isError,
    error,
  };
};

export default useHotFiltersData;
